"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ceil = ceil;
exports.clamp = clamp;
exports.cosh = void 0;
exports.floor = floor;
exports.lerp = lerp;
exports.log2 = void 0;
exports.modulo = modulo;
exports.round = round;
exports.solveLinearSystem = solveLinearSystem;
exports.squaredDistance = squaredDistance;
exports.squaredSegmentDistance = squaredSegmentDistance;
exports.toDegrees = toDegrees;
exports.toFixed = toFixed;
exports.toRadians = toRadians;

/**
 * @module ol/math
 */

/**
 * Takes a number and clamps it to within the provided bounds.
 * @param {number} value The input number.
 * @param {number} min The minimum value to return.
 * @param {number} max The maximum value to return.
 * @return {number} The input number if it is within bounds, or the nearest
 *     number within the bounds.
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
/**
 * Return the hyperbolic cosine of a given number. The method will use the
 * native `Math.cosh` function if it is available, otherwise the hyperbolic
 * cosine will be calculated via the reference implementation of the Mozilla
 * developer network.
 *
 * @param {number} x X.
 * @return {number} Hyperbolic cosine of x.
 */


var cosh = function () {
  // Wrapped in a iife, to save the overhead of checking for the native
  // implementation on every invocation.
  var cosh;

  if ('cosh' in Math) {
    // The environment supports the native Math.cosh function, use it…
    cosh = Math.cosh;
  } else {
    // … else, use the reference implementation of MDN:
    cosh = function cosh(x) {
      var y =
      /** @type {Math} */
      Math.exp(x);
      return (y + 1 / y) / 2;
    };
  }

  return cosh;
}();
/**
 * Return the base 2 logarithm of a given number. The method will use the
 * native `Math.log2` function if it is available, otherwise the base 2
 * logarithm will be calculated via the reference implementation of the
 * Mozilla developer network.
 *
 * @param {number} x X.
 * @return {number} Base 2 logarithm of x.
 */


exports.cosh = cosh;

var log2 = function () {
  // Wrapped in a iife, to save the overhead of checking for the native
  // implementation on every invocation.
  var log2;

  if ('log2' in Math) {
    // The environment supports the native Math.log2 function, use it…
    log2 = Math.log2;
  } else {
    // … else, use the reference implementation of MDN:
    log2 = function log2(x) {
      return Math.log(x) * Math.LOG2E;
    };
  }

  return log2;
}();
/**
 * Returns the square of the closest distance between the point (x, y) and the
 * line segment (x1, y1) to (x2, y2).
 * @param {number} x X.
 * @param {number} y Y.
 * @param {number} x1 X1.
 * @param {number} y1 Y1.
 * @param {number} x2 X2.
 * @param {number} y2 Y2.
 * @return {number} Squared distance.
 */


exports.log2 = log2;

function squaredSegmentDistance(x, y, x1, y1, x2, y2) {
  var dx = x2 - x1;
  var dy = y2 - y1;

  if (dx !== 0 || dy !== 0) {
    var t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);

    if (t > 1) {
      x1 = x2;
      y1 = y2;
    } else if (t > 0) {
      x1 += dx * t;
      y1 += dy * t;
    }
  }

  return squaredDistance(x, y, x1, y1);
}
/**
 * Returns the square of the distance between the points (x1, y1) and (x2, y2).
 * @param {number} x1 X1.
 * @param {number} y1 Y1.
 * @param {number} x2 X2.
 * @param {number} y2 Y2.
 * @return {number} Squared distance.
 */


function squaredDistance(x1, y1, x2, y2) {
  var dx = x2 - x1;
  var dy = y2 - y1;
  return dx * dx + dy * dy;
}
/**
 * Solves system of linear equations using Gaussian elimination method.
 *
 * @param {Array<Array<number>>} mat Augmented matrix (n x n + 1 column)
 *                                     in row-major order.
 * @return {Array<number>} The resulting vector.
 */


function solveLinearSystem(mat) {
  var n = mat.length;

  for (var i = 0; i < n; i++) {
    // Find max in the i-th column (ignoring i - 1 first rows)
    var maxRow = i;
    var maxEl = Math.abs(mat[i][i]);

    for (var r = i + 1; r < n; r++) {
      var absValue = Math.abs(mat[r][i]);

      if (absValue > maxEl) {
        maxEl = absValue;
        maxRow = r;
      }
    }

    if (maxEl === 0) {
      return null; // matrix is singular
    } // Swap max row with i-th (current) row


    var tmp = mat[maxRow];
    mat[maxRow] = mat[i];
    mat[i] = tmp; // Subtract the i-th row to make all the remaining rows 0 in the i-th column

    for (var j = i + 1; j < n; j++) {
      var coef = -mat[j][i] / mat[i][i];

      for (var k = i; k < n + 1; k++) {
        if (i == k) {
          mat[j][k] = 0;
        } else {
          mat[j][k] += coef * mat[i][k];
        }
      }
    }
  } // Solve Ax=b for upper triangular matrix A (mat)


  var x = new Array(n);

  for (var l = n - 1; l >= 0; l--) {
    x[l] = mat[l][n] / mat[l][l];

    for (var m = l - 1; m >= 0; m--) {
      mat[m][n] -= mat[m][l] * x[l];
    }
  }

  return x;
}
/**
 * Converts radians to to degrees.
 *
 * @param {number} angleInRadians Angle in radians.
 * @return {number} Angle in degrees.
 */


function toDegrees(angleInRadians) {
  return angleInRadians * 180 / Math.PI;
}
/**
 * Converts degrees to radians.
 *
 * @param {number} angleInDegrees Angle in degrees.
 * @return {number} Angle in radians.
 */


function toRadians(angleInDegrees) {
  return angleInDegrees * Math.PI / 180;
}
/**
 * Returns the modulo of a / b, depending on the sign of b.
 *
 * @param {number} a Dividend.
 * @param {number} b Divisor.
 * @return {number} Modulo.
 */


function modulo(a, b) {
  var r = a % b;
  return r * b < 0 ? r + b : r;
}
/**
 * Calculates the linearly interpolated value of x between a and b.
 *
 * @param {number} a Number
 * @param {number} b Number
 * @param {number} x Value to be interpolated.
 * @return {number} Interpolated value.
 */


function lerp(a, b, x) {
  return a + x * (b - a);
}
/**
 * Returns a number with a limited number of decimal digits.
 * @param {number} n The input number.
 * @param {number} decimals The maximum number of decimal digits.
 * @return {number} The input number with a limited number of decimal digits.
 */


function toFixed(n, decimals) {
  var factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
}
/**
 * Rounds a number to the nearest integer value considering only the given number
 * of decimal digits (with rounding on the final digit).
 * @param {number} n The input number.
 * @param {number} decimals The maximum number of decimal digits.
 * @return {number} The nearest integer.
 */


function round(n, decimals) {
  return Math.round(toFixed(n, decimals));
}
/**
 * Rounds a number to the next smaller integer considering only the given number
 * of decimal digits (with rounding on the final digit).
 * @param {number} n The input number.
 * @param {number} decimals The maximum number of decimal digits.
 * @return {number} The next smaller integer.
 */


function floor(n, decimals) {
  return Math.floor(toFixed(n, decimals));
}
/**
 * Rounds a number to the next bigger integer considering only the given number
 * of decimal digits (with rounding on the final digit).
 * @param {number} n The input number.
 * @param {number} decimals The maximum number of decimal digits.
 * @return {number} The next bigger integer.
 */


function ceil(n, decimals) {
  return Math.ceil(toFixed(n, decimals));
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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